import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Crown, Users, Edit, Search, Building2, MapPin, Download, FileText, Loader2 } from 'lucide-react';

export default function LeadershipPage() {
  const { api, isAdmin, canExportExcel, canExportPDF } = useAuth();
  const [mohallas, setMohallas] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMohalla, setSelectedMohalla] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('mohalla');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/leadership');
      setMohallas(response.data.mohallas);
      setSectors(response.data.sectors);
    } catch (error) {
      toast.error('Failed to fetch leadership data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Export handler
  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportType = activeTab === 'mohalla' ? 'mohalla' : 'sector';
      let url = `/leadership/export?type=${exportType}&format=${format}`;
      if (activeTab === 'sector' && selectedMohalla) {
        url += `&mohalla=${encodeURIComponent(selectedMohalla)}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create download
      const mimeType = format === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const fileName = `Leadership_${exportType}_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const filteredSectors = selectedMohalla 
    ? sectors.filter(s => s.mohalla === selectedMohalla)
    : sectors;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leadership-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-800">Masool / Musaid Management</h1>
          <p className="text-sm text-slate-500">Manage Jamaat leadership assignments for Mohallas and Sectors</p>
        </div>
        
        {/* Export Buttons */}
        {(canExportPDF || canExportExcel) && (
          <div className="flex gap-2">
            {canExportPDF && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('pdf')}
                disabled={exporting}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="export-pdf-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                PDF
              </Button>
            )}
            {canExportExcel && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className="text-green-600 border-green-200 hover:bg-green-50"
                data-testid="export-excel-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Excel
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="mohalla" className="data-[state=active]:bg-navy data-[state=active]:text-white">
            <Building2 className="w-4 h-4 mr-2" />
            Mohalla Leaders
          </TabsTrigger>
          <TabsTrigger value="sector" className="data-[state=active]:bg-navy data-[state=active]:text-white">
            <MapPin className="w-4 h-4 mr-2" />
            Sector Leaders
          </TabsTrigger>
        </TabsList>

        {/* Mohalla Leaders Tab */}
        <TabsContent value="mohalla">
          <Card className="border-0 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Mohalla Masool & Masoola
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mohalla</TableHead>
                      <TableHead>Masool (Male)</TableHead>
                      <TableHead>Masool Mobile</TableHead>
                      <TableHead>Masoola (Female)</TableHead>
                      <TableHead>Masoola Mobile</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8">
                          <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : mohallas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No mohallas found
                        </TableCell>
                      </TableRow>
                    ) : (
                      mohallas.map((mohalla) => (
                        <TableRow key={mohalla.id}>
                          <TableCell className="font-medium">{mohalla.name}</TableCell>
                          <TableCell>{mohalla.masool_name || <span className="text-slate-400">Not Assigned</span>}</TableCell>
                          <TableCell>{mohalla.masool_mobile || '-'}</TableCell>
                          <TableCell>{mohalla.masoola_name || <span className="text-slate-400">Not Assigned</span>}</TableCell>
                          <TableCell>{mohalla.masoola_mobile || '-'}</TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              <div className="flex gap-2 justify-end">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setEditingRole({ type: 'masool', mohalla: mohalla.name, current: mohalla.masool_name })}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Masool
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setEditingRole({ type: 'masoola', mohalla: mohalla.name, current: mohalla.masoola_name })}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Masoola
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sector Leaders Tab */}
        <TabsContent value="sector">
          <Card className="border-0 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Sector Musaid & Musaida
              </CardTitle>
              <Select value={selectedMohalla || "all"} onValueChange={(v) => setSelectedMohalla(v === "all" ? "" : v)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by Mohalla" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Mohallas</SelectItem>
                  {mohallas.map(m => (
                    <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mohalla</TableHead>
                      <TableHead>Sector</TableHead>
                      <TableHead>Musaid (Male)</TableHead>
                      <TableHead>Musaid Mobile</TableHead>
                      <TableHead>Musaida (Female)</TableHead>
                      <TableHead>Musaida Mobile</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="w-6 h-6 border-2 border-navy border-t-transparent rounded-full animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : filteredSectors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          No sectors found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSectors.map((sector) => (
                        <TableRow key={sector.id}>
                          <TableCell>{sector.mohalla}</TableCell>
                          <TableCell className="font-medium">{sector.name}</TableCell>
                          <TableCell>{sector.musaid_name || <span className="text-slate-400">Not Assigned</span>}</TableCell>
                          <TableCell>{sector.musaid_mobile || '-'}</TableCell>
                          <TableCell>{sector.musaida_name || <span className="text-slate-400">Not Assigned</span>}</TableCell>
                          <TableCell>{sector.musaida_mobile || '-'}</TableCell>
                          {isAdmin && (
                            <TableCell className="text-right">
                              <div className="flex gap-2 justify-end">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setEditingRole({ 
                                    type: 'musaid', 
                                    mohalla: sector.mohalla, 
                                    sector: sector.name,
                                    current: sector.musaid_name 
                                  })}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Musaid
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setEditingRole({ 
                                    type: 'musaida', 
                                    mohalla: sector.mohalla, 
                                    sector: sector.name,
                                    current: sector.musaida_name 
                                  })}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Musaida
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Leadership Dialog */}
      {editingRole && (
        <AssignLeadershipDialog
          role={editingRole}
          api={api}
          onClose={() => setEditingRole(null)}
          onSuccess={() => { fetchData(); setEditingRole(null); }}
        />
      )}
    </div>
  );
}

function AssignLeadershipDialog({ role, api, onClose, onSuccess }) {
  const [search, setSearch] = useState('');
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);

  const searchMembers = async (query) => {
    if (!query || query.length < 2) {
      setMembers([]);
      return;
    }
    try {
      const response = await api.get(`/members?search=${encodeURIComponent(query)}&limit=15`);
      setMembers(response.data.members);
    } catch (error) {
      console.error('Failed to search members');
    }
  };

  const handleAssign = async () => {
    if (!selectedMember) {
      toast.error('Please select a member');
      return;
    }

    setLoading(true);
    try {
      await api.post('/leadership/assign', {
        member_id: selectedMember.id,
        role_type: role.type,
        mohalla: role.mohalla,
        sector: role.sector || null
      });
      toast.success(`${role.type.charAt(0).toUpperCase() + role.type.slice(1)} assigned successfully`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign leadership');
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = role.type.charAt(0).toUpperCase() + role.type.slice(1);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Assign {roleLabel} - {role.sector || role.mohalla}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {role.current && (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                Current {roleLabel}: <span className="font-medium">{role.current}</span>
              </p>
              <p className="text-xs text-amber-600">Assigning a new member will replace the current one</p>
            </div>
          )}

          <div>
            <Label className="form-label">Search Member</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, ITS, mobile..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); searchMembers(e.target.value); }}
                className="pl-9"
              />
            </div>
          </div>

          {members.length > 0 && (
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              {members.map(member => (
                <div
                  key={member.id}
                  className={`p-3 cursor-pointer hover:bg-slate-50 border-b last:border-0 ${selectedMember?.id === member.id ? 'bg-blue-50' : ''}`}
                  onClick={() => setSelectedMember(member)}
                >
                  <p className="font-medium">{member.full_name}</p>
                  <p className="text-xs text-slate-500">
                    {member.mobile || 'No mobile'} • {member.mohalla || 'No Mohalla'} / {member.sector || 'No Sector'}
                  </p>
                </div>
              ))}
            </div>
          )}

          {selectedMember && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800">Selected: {selectedMember.full_name}</p>
              <p className="text-xs text-green-600">{selectedMember.mobile}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            onClick={handleAssign} 
            disabled={loading || !selectedMember}
            className="bg-navy hover:bg-navy-light"
          >
            {loading ? 'Assigning...' : `Assign ${roleLabel}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
